/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {CommonHelperService} from '../../service/common-helper.service';

@Component({
    selector: 'actions-cell-renderer',
    styleUrls: ['./cell-renderer.component.scss'],
    template: `
    @if (!hideCopyButton && hideRevertButton) {
      <button pButton size="small" pRipple type="button" tabindex="-1"
        class="action-button p-button-rounded p-button-text"
      (mousedown)="onActionMouseDown($event)"
      (click)="onCopyClick()" title="Copy Row"><i class="pi pi-copy" pButtonIcon></i></button>
    }
    @if (!hideDeleteButton) {
      <button pButton size="small" pRipple type="button" severity="danger" tabindex="-1"
        class="action-button p-button-rounded p-button-text"
      (mousedown)="onActionMouseDown($event)"
      (click)="onDeleteClick($event)" title="Delete Row"><i class="pi pi-trash" pButtonIcon></i></button>
    }
    @if (showApprovedLockedIcon) {
      <span class="action-button approved-locked-indicator" title="Approved row is locked">
        <i class="pi pi-lock"></i>
        <i class="pi pi-check approved-locked-check"></i>
      </span>
    }
    @if (!hideSaveButton) {
      <button pButton size="small" pRipple type="button" tabindex="-1"
        class="action-button p-button-rounded p-button-text p-button-black"
      (mousedown)="onActionMouseDown($event)"
      (click)="onSaveClick($event)" title="Save Row"><i class="pi pi-save" pButtonIcon></i></button>
    }
    @if (!hideRevertButton) {
      <button pButton size="small" pRipple type="button" severity="danger" tabindex="-1"
        class="action-button p-button-rounded p-button-text"
      (mousedown)="onActionMouseDown($event)"
      (click)="onRevertClick($event)" title="Revert Changes"><i class="pi pi-undo" pButtonIcon></i></button>
    }
    @if (!hideViewButton) {
      <button pButton size="small" pRipple type="button" tabindex="-1"
        class="action-button p-button-rounded p-button-text p-button-black"
      (mousedown)="onActionMouseDown($event)"
      (click)="onViewDetailsClick($event)" title="View Details"><i class="pi pi-eye" pButtonIcon></i></button>
    }
    `,
    styles: [`
    .action-button {
      width: 24px;
      min-width: 24px;
      height: 24px;
      position: relative;
    }
    button.action-button {
      padding: 0 !important;
    }
    .action-button + .action-button {
      margin-left: -6px;
    }
    .action-button:hover {
      z-index: 2;
    }
    .action-button .pi {
      font-size: 0.9rem;
    }
    .approved-locked-indicator {
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      color: #2e7d32;
      font-size: 0.95rem;
      opacity: 0.95;
    }
    .approved-locked-check {
      position: absolute;
      right: -2px;
      bottom: -3px;
      font-size: 0.52rem;
      background: transparent;
      border-radius: 0;
      padding: 0;
      line-height: 1;
      text-shadow: 0 0 1px #fff;
    }
  `],
    standalone: false
})
export class ActionsCellRendererComponent {

  params: any;
  fieldName: string;
  hideCopyButton = false;
  hideDeleteButton = false;
  hideSaveButton = false;
  hideRevertButton = false;
  hideViewButton = false;
  showApprovedLockedIcon = false;
  constructor(private confirmationService: ConfirmationService) {
  }

  agInit(params: any): void {
    this.params = params;
    this.fieldName = params.fieldForDeleteMessage;
    this.hideCopyButton = params.hideCopyButton;
    this.hideRevertButton = params.data.state === 'saved' || params.data.state === 'new' || !params.onRevertClick;
    this.hideSaveButton = params.data.state === 'saved' || !params.onSaveClick;
    this.hideViewButton = !params.onViewDetailsClick;
    const isApprovedLockedRow = !!params.isApprovedLockedRow?.(params.data);
    // hideDeleteButtonIfFieldExists is the field we check in our row to decide if we hide delete button
    // in workload timing, if momentum id exists we cannot delete the program
    this.hideDeleteButton = params.data.state === 'deleted' || !params.onDeleteClick || !this.hideSaveButton ||
                            params.data[params.hideDeleteButtonIfFieldExists] || isApprovedLockedRow;
    this.showApprovedLockedIcon = isApprovedLockedRow && this.hideDeleteButton && this.hideSaveButton;
  }

  onRevertClick(event: Event): void {
    this.params.onRevertClick?.(this.params);
  }

  onActionMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onSaveClick(event: Event): void {
    this.params.onSaveClick?.(this.params);
  }

  onViewDetailsClick(event: Event): void {
    this.params.onViewDetailsClick?.(this.params, false);
  }

  onDeleteClick(event: Event): void {
    // if new row, just delete
    if (!this.params.data.id || this.params.data.id === -1) {
      this.params.onDeleteClick?.(this.params);
      return;
    }
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Do you want to delete this record? (${CommonHelperService.getDeepValue(this.params.data, this.fieldName) || ''})`,
      header: 'Delete Confirmation',
      key: 'deleteRowConfirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: { severity: 'danger' },
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',

      accept: () => {
        this.params.onDeleteClick?.(this.params);
      }
    });
  }

  onCopyClick(): void {
    this.params.onCopyClick?.(this.params);
  }
}
